/**
 * Administration → Branding & white-label: the application name, logo, colours and "powered by"
 * line of this tenant. Everything is previewed live and applied to every screen and printed
 * document on save (src/core/branding is the single definition).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api.ts';
import { useI18n } from '../../lib/i18n.tsx';
import { useMeta } from '../../lib/meta.tsx';
import { useSession } from '../../lib/session.tsx';
import { useBranding } from '../../lib/branding.tsx';
import { Button, Card, ErrorState, Field, Spinner, useToast } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { PageHead } from '../../components/PageHead.tsx';
import { DEFAULT_BRANDING, MAX_LOGO_BYTES, brandLogo, brandingCssVars, dataUriBytes, normalizeBranding, readableOn, type Branding } from '../../../core/branding/branding.ts';

interface BrandingView { branding: Branding; issues: Array<{ field: string; message: string }>; tenant: { id: string; name: string; slug: string } }

const IMAGE_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];

/** Reads an image file as a data: URI; raster images wider than `maxWidth` or above the size limit are re-encoded smaller. */
async function fileToDataUri(file: File, maxWidth: number): Promise<string> {
  if (!IMAGE_TYPES.includes(file.type)) throw new Error('unsupported');
  const raw = await new Promise<string>((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = () => reject(r.error); r.readAsDataURL(file); });
  if (file.type === 'image/svg+xml') { if (dataUriBytes(raw) > MAX_LOGO_BYTES) throw new Error('too_large'); return raw; }
  const img = await new Promise<HTMLImageElement>((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error('unreadable')); i.src = raw; });
  if (img.width <= maxWidth && dataUriBytes(raw) <= MAX_LOGO_BYTES) return raw;
  const scale = Math.min(1, maxWidth / img.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  for (const [type, q] of [['image/png', undefined], ['image/webp', 0.9], ['image/jpeg', 0.85]] as Array<[string, number | undefined]>) {
    const out = canvas.toDataURL(type, q);
    if (out.startsWith(`data:${type}`) && dataUriBytes(out) <= MAX_LOGO_BYTES) return out;
  }
  throw new Error('too_large');
}

function LogoField({ label, hint, value, onChange, maxWidth, mark }: { label: string; hint: string; value: string | null; onChange: (v: string | null) => void; maxWidth: number; mark?: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    try { onChange(await fileToDataUri(file, maxWidth)); }
    catch (e) { toast.error(new Error((e as Error).message === 'too_large' ? t('brand.tooLarge', { kb: MAX_LOGO_BYTES / 1024 }) : t('brand.unsupported'))); }
    finally { if (input.current) input.current.value = ''; }
  };
  return (
    <Field label={label} hint={hint}>
      <div className="logo-field" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void pick(e.dataTransfer.files[0]); }}>
        <div className={`logo-preview ${mark ? 'mark' : ''}`}>{value ? <img src={value} alt="" /> : <span className="muted">{t('brand.noLogo')}</span>}</div>
        <div className="row" style={{ gap: 8 }}>
          <Button icon="Upload" onClick={() => input.current?.click()}>{value ? t('brand.replace') : t('brand.upload')}</Button>
          {value && <Button icon="X" onClick={() => onChange(null)}>{t('common.remove')}</Button>}
          <input ref={input} type="file" accept={IMAGE_TYPES.join(',')} hidden onChange={(e) => void pick(e.target.files?.[0])} />
        </div>
      </div>
    </Field>
  );
}

function ColourField({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = (v: string) => { setText(v); if (/^#[0-9a-fA-F]{6}$/.test(v)) onChange(v.toLowerCase()); };
  return (
    <Field label={label} hint={hint}>
      <div className="row colour-field">
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'} onChange={(e) => commit(e.target.value)} aria-label={label} />
        <input className="input" dir="ltr" value={text} onChange={(e) => commit(e.target.value)} maxLength={7} style={{ width: 120 }} />
      </div>
    </Field>
  );
}

/** A miniature of the shell and of a printed document header with the edited branding applied. */
function Preview({ b, companyName }: { b: Branding; companyName: string }) {
  const { t, locale } = useI18n();
  const vars = brandingCssVars(b) as Record<string, string>;
  const logo = brandLogo(b, 'logo');
  const tagline = locale === 'ar' ? b.taglineAr ?? b.tagline : b.tagline;
  return (
    <div className="brand-preview" style={vars as React.CSSProperties}>
      <div className="mini-shell">
        <div className="mini-sidebar" style={{ background: vars['--sidebar'], color: vars['--sidebar-fg'] }}>
          <div className="mini-brand">{logo ? <span className="brand-plate"><img src={logo} alt="" /></span> : <img src="/logo.svg" alt="" width={28} height={28} />}<b>{b.appName}</b>{tagline && <small style={{ color: vars['--sidebar-muted'] }}>{tagline}</small>}</div>
          <div className="mini-nav"><span style={{ background: vars['--sidebar-active'], color: vars['--sidebar-active-fg'] }}>{t('nav.home')}</span><span>{t('brand.sampleModule')}</span><span>{t('brand.sampleModule2')}</span></div>
        </div>
        <div className="mini-main">
          <div className="mini-header"><span className="mini-search" /><span className="mini-avatar" style={{ background: vars['--primary-soft'], color: vars['--primary'] }}>SA</span></div>
          <div className="mini-body">
            <div className="mini-kpi"><small>{t('brand.sampleKpi')}</small><b style={{ color: vars['--primary'] }}>EGP 1.2M</b></div>
            <span className="mini-btn" style={{ background: vars['--primary'], color: readableOn(b.primaryColor) }}>{t('common.save')}</span>
            <span className="mini-dot" style={{ background: vars['--accent'] }} />
          </div>
          <div className="mini-footer"><b>{b.appName}</b>{b.poweredBy && <span>· {t('ui.poweredBy', { product: b.productName })}</span>}</div>
        </div>
      </div>
      <div className="mini-doc" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="mini-doc-head" style={{ borderColor: b.primaryColor }}>
          {logo && <img src={logo} alt="" />}
          <div><b style={{ color: b.primaryColor }}>{companyName}</b><small>{t('brand.sampleDoc')}</small></div>
        </div>
        <div className="mini-doc-foot">{t('brand.sampleFooter')}{b.poweredBy ? ` · ${t('ui.poweredBy', { product: b.productName })}` : ''}</div>
      </div>
    </div>
  );
}

export function BrandingPage() {
  const { t } = useI18n();
  const toast = useToast();
  const { nav } = useMeta();
  const { can, session, reload } = useSession();
  const { refresh: refreshBranding } = useBranding();
  const [view, setView] = useState<BrandingView | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [form, setForm] = useState<Branding>(DEFAULT_BRANDING);
  const [busy, setBusy] = useState(false);
  const editable = can('admin.settings.configure');
  const admin = nav.find((m) => m.code === 'admin');
  const load = async () => {
    setError(null);
    try { const v = await api.get<BrandingView>('admin/branding'); setView(v); setForm(v.branding); }
    catch (e) { setError(e as Error); }
  };
  useEffect(() => { void load(); }, []);
  const dirty = useMemo(() => !!view && JSON.stringify(view.branding) !== JSON.stringify(form), [view, form]);
  const set = <K extends keyof Branding>(k: K, v: Branding[K]) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const { issues } = normalizeBranding(form);
    if (issues.length) { toast.error(new Error(issues.map((i) => `${i.field}: ${i.message}`).join('; '))); return; }
    setBusy(true);
    try {
      const v = await api.put<BrandingView>('admin/branding', { branding: form, reason: 'Branding changed in administration' });
      setView(v); setForm(v.branding);
      await Promise.all([reload(), refreshBranding()]);
      toast.success(t('brand.saved'));
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  if (error) return <ErrorState error={error} retry={() => void load()} />;
  if (!view) return <Spinner />;
  return (
    <div className="stack">
      <PageHead title={t('brand.title')} sub={t('brand.sub')} crumbs={[{ label: admin?.label ?? 'Administration', to: '/admin' }]}
        actions={editable && <>
          <Button icon="RotateCcw" onClick={() => setForm({ ...DEFAULT_BRANDING, appName: view.tenant.name })}>{t('brand.reset')}</Button>
          <Button variant="primary" icon="Save" busy={busy} disabled={!dirty} onClick={() => void save()}>{t('common.save')}</Button>
        </>} />
      {!!view.issues.length && <div className="banner amber"><Icon name="TriangleAlert" /><span>{t('brand.storedIssues')}: {view.issues.map((i) => `${i.field} — ${i.message}`).join('; ')}</span></div>}
      <div className="brand-layout">
        <div className="stack">
          <Card title={t('brand.identity')}>
            <div className="fields">
              <Field label={t('brand.appName')} required hint={t('brand.appNameHint')}><input className="input" value={form.appName} maxLength={60} disabled={!editable} onChange={(e) => set('appName', e.target.value)} /></Field>
              <Field label={t('brand.tagline')}><input className="input" value={form.tagline ?? ''} maxLength={120} disabled={!editable} onChange={(e) => set('tagline', e.target.value || null)} /></Field>
              <Field label={t('brand.taglineAr')}><input className="input" dir="rtl" value={form.taglineAr ?? ''} maxLength={120} disabled={!editable} onChange={(e) => set('taglineAr', e.target.value || null)} /></Field>
              <Field label={t('brand.poweredBy')} hint={t('brand.poweredByHint')}>
                <label className="check"><input type="checkbox" checked={form.poweredBy} disabled={!editable} onChange={(e) => set('poweredBy', e.target.checked)} /><span>{t('ui.poweredBy', { product: form.productName })}</span></label>
              </Field>
              {form.poweredBy && <Field label={t('brand.productName')}><input className="input" value={form.productName} maxLength={60} disabled={!editable} onChange={(e) => set('productName', e.target.value)} /></Field>}
            </div>
          </Card>
          <Card title={t('brand.logos')}>
            <div className="fields">
              <LogoField label={t('brand.logo')} hint={t('brand.logoHint')} value={form.logo} onChange={(v) => editable && set('logo', v)} maxWidth={1200} />
              <LogoField label={t('brand.mark')} hint={t('brand.markHint')} value={form.logoMark} onChange={(v) => editable && set('logoMark', v)} maxWidth={256} mark />
            </div>
          </Card>
          <Card title={t('brand.colours')}>
            <div className="fields cols-3">
              <ColourField label={t('brand.primary')} value={form.primaryColor} onChange={(v) => editable && set('primaryColor', v)} hint={t('brand.primaryHint')} />
              <ColourField label={t('brand.accent')} value={form.accentColor} onChange={(v) => editable && set('accentColor', v)} hint={t('brand.accentHint')} />
              <ColourField label={t('brand.sidebar')} value={form.sidebarColor} onChange={(v) => editable && set('sidebarColor', v)} hint={t('brand.sidebarHint')} />
            </div>
          </Card>
        </div>
        <Card title={t('brand.preview')} className="brand-preview-card">
          <Preview b={normalizeBranding(form).branding} companyName={session?.tenant?.name ?? view.tenant.name} />
          <div className="field-hint" style={{ marginTop: 10 }}>{t('brand.previewHint')}</div>
        </Card>
      </div>
    </div>
  );
}
